import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Col, FormGroup, InputGroupText, Label, Input } from 'reactstrap';
import { Field, reduxForm, Form, change, formValueSelector } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import FormItem from '../../../common/forms/FormItem';
import { isRequired, validateNumber } from '../../../../utils/validators';
import TextInput from '../../../common/forms/input/TextInput';
import DynamicForm from '../../../common/forms/DynamicForm';
import { CURRENCY } from '../../../../config/locale';
import { EXPECTED_AMOUNT_TO_DEPOSIT_INFO_MESSAGE } from '../../../../config/messages';
import {
  generateBankAccountColumns,
  DAILY_LIQUIDATION_FORM_COLUMNS,
  NOTES_FORM_COLUMNS,
} from '../../../../config/dynamicFormFields';
import LiquidationDifferenceReasonSelect from '../../../common/forms/select/LiquidationDifferenceReasonSelect';
import DepositLiquidationsReource from './resource/DepositLiquitionsResource';
import NoteListResource from '../../../common/resource/NoteListResource';
import {
  DEPOSIT_TYPE,
  DEPOSIT_TYPE_OPTIONS,
  DEPOSIT_FILE_TYPE_PATTERN,
  DEPOSIT_MIGRATION_STATUS,
} from '../../../../config/constants';
import Select from '../../../common/forms/select/Select';
import Loader from '../../../common/Loader';
import DepositVoucherResource from './resource/DepositVoucherResource';
import InformationText from '../../../common/informative/InformationText';
import FileDetailResource from '../../../common/resource/FileDetailResource';
import { numberFormatter } from '../../../../utils/number';
import FormFooter from '../../../common/forms/FormFooter';
import { verifyDeposit } from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';

const selector = formValueSelector('DepositForm');

const isMigratedBankAccountRow = (rowValues) =>
  !!rowValues &&
  rowValues.migrationStatus === DEPOSIT_MIGRATION_STATUS.MIGRATED.value;

const validateBankAccounts = (values) => {
  const errors = {};

  const { depositBankAccountList } = values;

  if (!depositBankAccountList || !depositBankAccountList.length) {
    errors.depositBankAccountListElements =
      'Debe ingresar al menos una cuenta bancaria';
  }

  return errors;
};

export const DepositForm = ({
  handleSubmit,
  onSubmit,
  expectedAmountToDeposit,
  actualAmountDeposited,
  initialValues,
  depositNoteList,
  loading,
  voucher,
  agencyCommission,
  agencyCommissionTaxes,
  dispatchChange,
  depositBankAccountList,
}) => {
  const [amountDeposited, setAmountDeposited] = useState(0);
  const [showLiquidationDifference, setShowLiquidationDifference] =
    useState(false);
  const [editingMode, setEditingMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [depositType, setDepositType] = useState(null);
  const [verifyingIndex, setVerifyingIndex] = useState(null);

  const hasRegisteredDepositDifference = !!(
    initialValues && initialValues.depositDifferenceReasonId
  );

  const isMountedRef = useRef(false);

  useLayoutEffect(() => {
    if (initialValues) {
      setEditingMode(true);
      setAmountDeposited(initialValues.actualAmountDeposited);
      setDepositType(initialValues.depositType.value);

      if (
        Number(initialValues.expectedAmountToDeposit) !==
          Number(initialValues.actualAmountDeposited) &&
        initialValues.depositType.value !== DEPOSIT_TYPE.COMMISSION.value
      ) {
        setShowLiquidationDifference(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    dispatchChange(
      'DepositForm',
      'expectedAmountToDeposit',
      expectedAmountToDeposit,
    );
    dispatchChange('DepositForm', 'actualAmountDeposited', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedAmountToDeposit]);

  useEffect(() => {
    dispatchChange('DepositForm', 'agencyCommission', agencyCommission);
    dispatchChange(
      'DepositForm',
      'agencyCommissionTaxes',
      agencyCommissionTaxes,
    );
    dispatchChange(
      'DepositForm',
      'totalToBill',
      (+agencyCommission + +agencyCommissionTaxes).toFixed(2),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyCommission, agencyCommissionTaxes]);

  const onChangeVoucher = (event) => setSelectedFile(event.target.files[0]);

  const onChangeDepositType = (option) =>
    setDepositType(option ? option.value : null);

  const validateImageFile = () => {
    let valid = true;

    if (selectedFile && !selectedFile.type.match(DEPOSIT_FILE_TYPE_PATTERN)) {
      toastr.error('Error', 'Sólo se permiten archivo de tipo: imagen/pdf');
      valid = false;
    }

    return valid;
  };

  const validateDepositDifference = (
    currentAmountDeposited = amountDeposited,
  ) => {
    let newShowLiquidationDifference = false;

    if (depositType !== DEPOSIT_TYPE.COMMISSION.value) {
      newShowLiquidationDifference =
        Number(currentAmountDeposited) !== Number(expectedAmountToDeposit);

      setShowLiquidationDifference(newShowLiquidationDifference);
    }

    return newShowLiquidationDifference;
  };

  const onBlurCashOnHand = (currentAmountDeposited) => {
    validateDepositDifference(currentAmountDeposited);
  };

  const onCheckDiscrepancy = () => {
    const actual = Number(actualAmountDeposited || 0);
    const expected = Number(expectedAmountToDeposit || 0);

    dispatchChange(
      'DepositForm',
      'actualAmountDeposited',
      actualAmountDeposited,
    );

    if (depositType === DEPOSIT_TYPE.COMMISSION.value) {
      setAmountDeposited(actual);
      setShowLiquidationDifference(false);
      return false;
    }

    const show = actual !== expected;

    setAmountDeposited(actual);
    setShowLiquidationDifference(show);

    return show;
  };

  const onChangeVerifiableField = (index) => {
    dispatchChange(
      'DepositForm',
      `depositBankAccountList[${index}].verified`,
      false,
    );
  };

  const handleVerifyDeposit = async (index) => {
    const row = depositBankAccountList && depositBankAccountList[index];

    if (
      !row ||
      !row.bankAccountId ||
      !row.operationNumber ||
      !row.depositDate
    ) {
      toastr.warning(
        'Atención',
        'Complete cuenta bancaria, número de operación y fecha antes de verificar',
      );
      return;
    }

    setVerifyingIndex(index);

    try {
      const result = await verifyDeposit({
        bankAccountId: row.bankAccountId.value,
        transactionId: row.operationNumber,
        operationDate: tzNormalizeDate({
          date: row.depositDate,
          format: 'DD-MM-YYYY',
        }),
      });

      if (result) {
        if (result.operationDate) {
          dispatchChange(
            'DepositForm',
            `depositBankAccountList[${index}].depositDate`,
            tzNormalizeDate({ date: result.operationDate }),
          );
        }
        dispatchChange(
          'DepositForm',
          `depositBankAccountList[${index}].verified`,
          true,
        );
        dispatchChange(
          'DepositForm',
          `depositBankAccountList[${index}].actualAmountDeposited`,
          result.amount,
        );
        toastr.success(
          'Depósito verificado',
          `Contrato: ${result.contractNumber} - Monto: ${result.amount} - N° Orden: ${result.transactionId}`,
        );
      }
    } finally {
      setVerifyingIndex(null);
    }
  };

  const handleDepositDifference = (formValues) => {
    const newFormValues = {
      depositDate: formValues.depositDate,
      accountNumber: formValues.accountNumber,
      bankName: formValues.bankName,
      operationNumber: formValues.operationNumber,
      expectedAmountToDeposit,
      actualAmountDeposited: formValues.actualAmountDeposited,
      depositDifferenceDescription: formValues.depositDifferenceDescription,
      depositDifferenceReasonId: formValues.depositDifferenceReasonId,
      dailyLiquidationSessionSet: formValues.dailyLiquidationSessionSet,
      depositType: formValues.depositType,
      depositBankAccountList: formValues.depositBankAccountList,
    };

    if (editingMode) {
      newFormValues.depositNoteList = formValues.depositNoteList;
      newFormValues.settled = formValues.settled;
    }

    if (
      !formValues.dailyLiquidationSessionSet ||
      !formValues.dailyLiquidationSessionSet.length
    ) {
      toastr.error('Error', 'Debe seleccionar al menos una liquidación');
    } else if (validateDepositDifference()) {
      if (formValues.depositDifferenceReasonId) {
        newFormValues.depositDifferenceAmount =
          +expectedAmountToDeposit - +amountDeposited;

        if (validateImageFile()) {
          newFormValues.file = selectedFile;
          onSubmit(newFormValues);
        }
      }
    } else {
      delete newFormValues.depositDifferenceReasonId;
      delete newFormValues.depositDifferenceDescription;

      if (validateImageFile()) {
        newFormValues.file = selectedFile;
        onSubmit(newFormValues);
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  let depositDifference;

  if (showLiquidationDifference) {
    depositDifference = (
      <Fragment>
        <h2>Discrepancia</h2>
        <h5>
          Monto de Discrepancia:{' '}
          {numberFormatter({
            value: +expectedAmountToDeposit - +amountDeposited,
          })}
        </h5>
        <FormGroup row>
          <FormItem label="Razón" required>
            <Field
              name="depositDifferenceReasonId"
              component={LiquidationDifferenceReasonSelect}
              isDisabled={hasRegisteredDepositDifference}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Descripción">
            <Field
              name="depositDifferenceDescription"
              component={TextInput}
              disabled={hasRegisteredDepositDifference}
              type="textarea"
            />
          </FormItem>
        </FormGroup>
      </Fragment>
    );
  }

  let liquidationTable = (
    <Fragment>
      <h3>Liquidaciones</h3>
      <DynamicForm
        name="dailyLiquidationSessionSet"
        columns={DAILY_LIQUIDATION_FORM_COLUMNS}
      />
    </Fragment>
  );

  const bankAccountColumns = generateBankAccountColumns({
    onClickVerify: handleVerifyDeposit,
    onChangeVerifiableField,
    verifyingIndex,
  });

  const bankAccountTable = (
    <Fragment>
      <h3>Cuentas Bancarias</h3>
      <DynamicForm
        name="depositBankAccountList"
        columns={bankAccountColumns}
        showAddButton={!editingMode}
        showRemoveButton={(rowValues) => !isMigratedBankAccountRow(rowValues)}
      />
      <Field
        name="depositBankAccountListElements"
        component={TextInput}
        type="hidden"
      />
    </Fragment>
  );
  let editingFields = null;

  if (editingMode) {
    liquidationTable = (
      <DepositLiquidationsReource
        dailyLiquidationSessionSet={initialValues.dailyLiquidationSessionSet}
      />
    );

    editingFields = (
      <Fragment>
        <br />
        <h3>Notas</h3>
        <NoteListResource values={depositNoteList} />
        <DynamicForm name="depositNoteList" columns={NOTES_FORM_COLUMNS} />
        <FormGroup row>
          <FormItem label="Estado">
            <Label>
              <Field name="settled" component="input" type="checkbox" />{' '}
              Aprobado
            </Label>
          </FormItem>
        </FormGroup>
      </Fragment>
    );
  }

  const fileDetailResource = <FileDetailResource file={selectedFile} />;

  let commissionFields = null;

  if (agencyCommission > 0) {
    commissionFields = (
      <>
        <FormGroup row>
          <FormItem label="Comisión" required>
            <Field
              name="agencyCommission"
              component={TextInput}
              type="text"
              disabled
              placeholder="0.00"
              validate={[isRequired, validateNumber]}
              value={agencyCommission}
              append={<InputGroupText>{CURRENCY}</InputGroupText>}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="IGV" required>
            <Field
              name="agencyCommissionTaxes"
              component={TextInput}
              type="text"
              disabled
              placeholder="0.00"
              validate={[isRequired, validateNumber]}
              value={agencyCommissionTaxes}
              append={<InputGroupText>{CURRENCY}</InputGroupText>}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Total a Facturar" required>
            <Field
              name="totalToBill"
              component={TextInput}
              type="text"
              disabled
              placeholder="0.00"
              validate={[isRequired, validateNumber]}
              append={<InputGroupText>{CURRENCY}</InputGroupText>}
            />
          </FormItem>
        </FormGroup>
      </>
    );
  }

  return (
    <Form onSubmit={handleSubmit(handleDepositDifference)}>
      {liquidationTable}
      <FormGroup row>
        <FormItem label="Tipo de Depósito">
          <Field
            name="depositType"
            component={Select}
            isDisabled={editingMode}
            onChange={onChangeDepositType}
            options={DEPOSIT_TYPE_OPTIONS}
            isClearable
          />
        </FormItem>
      </FormGroup>
      {bankAccountTable}
      {commissionFields}
      <FormGroup row>
        <FormItem label="Monto Esperado para Depositar" required>
          <Field
            name="expectedAmountToDeposit"
            component={TextInput}
            type="text"
            disabled
            placeholder="0.00"
            validate={[isRequired, validateNumber]}
            value={expectedAmountToDeposit}
            append={<InputGroupText>{CURRENCY}</InputGroupText>}
          />
          <InformationText text={EXPECTED_AMOUNT_TO_DEPOSIT_INFO_MESSAGE} />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Importe Real Depositado" required>
          <Field
            name="actualAmountDeposited"
            component={TextInput}
            type="text"
            placeholder="0.00"
            append={<InputGroupText>{CURRENCY}</InputGroupText>}
            disabled
            value={amountDeposited}
            onChange={(e) => {
              setAmountDeposited(e.target.value);
              onBlurCashOnHand(e.target.value);
            }}
            validate={[isRequired, validateNumber]}
          />
          <button
            type="button"
            className="btn btn-warning"
            style={{ marginTop: 8 }}
            onClick={onCheckDiscrepancy}
          >
            Calcular discrepancia
          </button>
        </FormItem>
      </FormGroup>
      {depositDifference}
      {editingFields}
      <FormGroup row>
        <Col sm={3} xs={12}>
          <Label>Comprobante del Depósito</Label>
        </Col>
        <Col sm={9} xs={12}>
          <Input
            name="voucher"
            type="file"
            onChange={(e) => onChangeVoucher(e)}
          />
          {fileDetailResource}
        </Col>
      </FormGroup>
      <DepositVoucherResource file={voucher} />
      <FormFooter />
    </Form>
  );
};

DepositForm.hasValidLiquidation = ({ value }, allValues) => {
  let duplicateCount = 0;
  allValues.dailyLiquidationSessionSet.forEach((liquidation) => {
    const { dailyLiquidationSession } = liquidation;
    if (dailyLiquidationSession && dailyLiquidationSession.value === value) {
      duplicateCount += 1;
    }
  });

  return duplicateCount >= 2 ? 'Liquidación repetida' : undefined;
};

DepositForm.propTypes = {
  loading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  expectedAmountToDeposit: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  actualAmountDeposited: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  agencyCommission: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  agencyCommissionTaxes: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  initialValues: PropTypes.shape({
    actualAmountDeposited: PropTypes.number,
    expectedAmountToDeposit: PropTypes.number,
    dailyLiquidationSessionSet: PropTypes.arrayOf(
      PropTypes.shape({
        agency: PropTypes.shape({
          name: PropTypes.string,
        }),
        createDate: PropTypes.number,
        cashToDeposit: PropTypes.number,
        operatorUser: PropTypes.shape({
          customer: PropTypes.shape({
            fullName: PropTypes.string,
          }),
        }),
        dailyLiquidationSession: PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
      }),
    ),
    depositType: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    depositDifferenceReasonId: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    depositDifferenceDescription: PropTypes.string,
  }),
  depositNoteList: PropTypes.arrayOf(
    PropTypes.shape({
      note: PropTypes.string,
    }),
  ),
  voucher: PropTypes.instanceOf(Immutable.Set),
  depositBankAccountList: PropTypes.arrayOf(
    PropTypes.shape({
      bankAccountId: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
        bankId: PropTypes.number,
      }),
      operationNumber: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      depositDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      verified: PropTypes.bool,
      id: PropTypes.number,
      migrationStatus: PropTypes.string,
    }),
  ),
};

DepositForm.defaultProps = {
  expectedAmountToDeposit: 0,
  agencyCommission: 0,
  agencyCommissionTaxes: 0,
  initialValues: null,
  depositNoteList: null,
  voucher: null,
  actualAmountDeposited: 0,
  depositBankAccountList: [],
};

const mapStateToProps = (state, ownProps) => {
  const { initialValues } = ownProps;

  const dailyLiquidationSessionSet = selector(
    state,
    'dailyLiquidationSessionSet',
  );

  const depositBankAccountList = selector(state, 'depositBankAccountList');

  let expectedAmountToDeposit = 0;
  let agencyCommission = 0;
  let agencyCommissionTaxes = 0;
  let actualAmountDeposited = 0;

  if (initialValues) {
    const {
      expectedAmountToDeposit: initialExpected = 0,
      agencyCommission: initialCommission = 0,
      agencyCommissionTaxes: initialCommissionTaxes = 0,
      actualAmountDeposited: initialActual = 0,
    } = initialValues;

    expectedAmountToDeposit = Number(initialExpected);
    agencyCommission = Number(initialCommission);
    agencyCommissionTaxes = Number(initialCommissionTaxes);
    actualAmountDeposited = Number(initialActual);
  } else if (
    dailyLiquidationSessionSet &&
    Array.isArray(dailyLiquidationSessionSet)
  ) {
    dailyLiquidationSessionSet.forEach(({ dailyLiquidationSession }) => {
      if (!dailyLiquidationSession) return;

      const { cashOnHand, commission, commissionTaxes } =
        dailyLiquidationSession;

      expectedAmountToDeposit += Number(cashOnHand || 0);
      agencyCommission += Number(commission || 0);
      agencyCommissionTaxes += Number(commissionTaxes || 0);
    });
  }

  if (depositBankAccountList && Array.isArray(depositBankAccountList)) {
    actualAmountDeposited = 0;

    depositBankAccountList.forEach(({ actualAmountDeposited: rowAmount }) => {
      const v = parseFloat(rowAmount || 0);
      if (!Number.isNaN(v)) {
        actualAmountDeposited += v;
      }
    });
  }

  return {
    expectedAmountToDeposit: expectedAmountToDeposit.toFixed(2),
    agencyCommission: agencyCommission.toFixed(2),
    agencyCommissionTaxes: agencyCommissionTaxes.toFixed(2),
    actualAmountDeposited: actualAmountDeposited.toFixed(2),
    loading: !state.SalesUnit.Deposit.getIn(['current', 'activity']).isEmpty(),
    depositBankAccountList: depositBankAccountList || [],
  };
};

const mapDispatchToProps = {
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: 'DepositForm',
  validate: validateBankAccounts,
})(DepositForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
